import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAbba } from "@foundation/abba-client";
import { useFortress } from "@foundation/fortress";
import { useRegUIClient } from "@foundation/reg-ui-client";
import {
    endStatsigPerfLog,
    startStatsigPerfLog,
    useLayer,
    useStatsigClient,
} from "@foundation/statsig";

import { TRANSPORT_COUNT_LIMIT } from "~constants";
import { TRANSLATION_LANGUAGES } from "~constants/i18n";
import {
    selectCurrentStory,
    selectStoryIds,
    storyFetched,
} from "~redux/features/stories-slice";
import { selectLanguagePreference } from "~redux/features/translation-slice";
import { getFortressState } from "~utils/getFortressState";
import getPersonalizationClient, {
    getPersonalizationParams,
} from "~utils/personalization";

import styles from "./observer.module.css";

export function TransporterObserver() {
    const containerRef = useRef(null);
    const { accesslistMembership } = useAbba();
    const dispatch = useDispatch();

    const fortressContext = useFortress();
    const { client: regUIClient } = useRegUIClient();
    const { isAllowed } = getFortressState(fortressContext);
    const isBBGSubscriber = regUIClient?.isBBGSubscriber();
    const isBWSubscriber = regUIClient?.isBWSubscriber();

    const currentStory = useSelector(selectCurrentStory);
    const storyIds = useSelector(selectStoryIds);
    const languagePreference = useSelector(selectLanguagePreference);
    const [hasTransported, setHasTransported] = useState(false);

    const transportLayer = useLayer("article-transporter");
    const statsig = useStatsigClient();

    const fetchTransportedStory = useCallback(
        async (assetID: string) => {
            try {
                const story = await fetch(
                    `/article/api/story/id/${assetID}?languagePreference=${
                        languagePreference || TRANSLATION_LANGUAGES.EN
                    }`
                ).then((res) => res.json());

                return story;
            } catch {
                return null;
            }
        },
        [languagePreference]
    );

    const doTransport = async () => {
        if (!currentStory) return;

        if (
            storyIds.length >= TRANSPORT_COUNT_LIMIT ||
            statsig.client.loadingStatus !== "Ready"
        ) {
            // Should we unobserve here?
            return;
        }

        startStatsigPerfLog("SS-transportLayerGet");
        const isEnabledInTransportLayer = transportLayer.get("enabled", true);
        endStatsigPerfLog("SS-transportLayerGet");

        statsig.logEvent(
            "reached_transport_intersection_trigger",
            storyIds.length,
            {
                storyId: currentStory.id,
                brand: currentStory.brand,
                headline: currentStory.headline,
                isTrending: String(currentStory.isTrending),
                // Including to verify bucket exposure tracking matches logged events
                transportEnabled: String(isEnabledInTransportLayer),
                isAllowed: String(isAllowed),
                isBBGSubscriber: String(isBBGSubscriber),
                isBWSubscriber: String(isBWSubscriber),
                accesslistMembership: String(accesslistMembership),
            }
        );

        if (!isEnabledInTransportLayer) {
            return;
        }

        try {
            startStatsigPerfLog("SS-transport");
            const personalizationParams = getPersonalizationParams(
                currentStory,
                storyIds
            );

            const pznStories = await getPersonalizationClient().getRecommended(
                "cfru",
                personalizationParams
            );
            const [pznStory] = pznStories;

            if (!pznStory) return;

            const story = await fetchTransportedStory(pznStory.assetID);

            dispatch(storyFetched(story));
            endStatsigPerfLog("SS-transport");

            // Should we unobserve here?
            // observer.unobserve(containerRef.current as unknown as Element);
        } catch (error) {
            window?.newrelic?.noticeError(error);

            // eslint-disable-next-line
            console.error(error);
        }
    };

    const intersectionHandler: IntersectionObserverCallback = useCallback(
        (entries, observer) => {
            const [entry] = entries;
            const isIntersecting = entry.isIntersecting;

            if (
                !observer ||
                !isIntersecting ||
                !containerRef.current ||
                hasTransported
            ) {
                return;
            }

            setHasTransported(true);
        },
        [hasTransported]
    );

    useEffect(() => {
        if (!hasTransported) return;

        doTransport();
    }, [hasTransported]);

    useEffect(() => {
        if (!isAllowed) return undefined;
        if (!accesslistMembership && !isBBGSubscriber && !isBWSubscriber) {
            return undefined;
        }

        if (statsig.client.loadingStatus !== "Ready") {
            return undefined;
        }

        // Bucket immediately when we know transporter feature enabled
        startStatsigPerfLog("SS-transportLayerGet");
        transportLayer.get("enabled", true);
        endStatsigPerfLog("SS-transportLayerGet");

        const observerOptions = {
            root: null,
            rootMargin: "1250px",
            threshold: 1,
        };

        const observer = new IntersectionObserver(
            intersectionHandler,
            observerOptions
        );

        const container = containerRef.current;

        if (container) {
            observer.observe(container);
        }

        return () => {
            if (container) {
                observer.unobserve(container);
            }
        };
    }, [
        containerRef,
        isAllowed,
        isBBGSubscriber,
        isBWSubscriber,
        accesslistMembership,
        statsig.client.loadingStatus,
        transportLayer,
        intersectionHandler,
    ]);

    return <div className={styles.observer} ref={containerRef} />;
}
